import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	Box,
	CircularProgress,
	Dialog,
	DialogContent,
	Divider,
	Grid,
	IconButton,
	Stack,
	Typography,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useGlobalization } from '@Translations';
import {
	getDeteriorationModelGroupsResults,
	getDeteriorationModelGroupsResultsChart,
} from '@Services/deteriorationModels.services';
import { GetDeteriorationModelGroupsResultsPaginatedRequests } from '@Types/deteriorationModels.types';
import { InfoTooltip } from '@Components/deteriorationModel/utils/InfoTooltip';
import { GRADE_CONFIG } from '@Components/deteriorationModel/utils/gradeConfig';
import { GradeChip } from '@Components/deteriorationModel/utils/GradeChip';
import { CurvePoint } from '@Components/deteriorationModel/utils/curveSeries';
import { WeibullFitNotes } from '@Components/deteriorationModel/utils/WeibullFitNotes';
import { isFlatSeries, WeibullParams } from '@Components/deteriorationModel/utils/weibullFit';
import { buildCurveChartOptions } from '@Components/deteriorationModel/utils/curveChart';

// =============================================================================
// TYPES
// =============================================================================

export interface AssetCurveTarget {
	assetId: string;
	/** Underlying asset-group id, used to resolve the group's survival curve unambiguously. */
	assetGroupId?: string;
	assetGroupName: string;
	age?: string;
	failureProbability?: string;
	rul?: string;
	grade?: string;
}

interface AssetCurveDialogProps {
	asset: AssetCurveTarget | null;
	open: boolean;
	onClose: () => void;
	onPrev: () => void;
	onNext: () => void;
	hasPrev: boolean;
	hasNext: boolean;
	modelId: string;
	runId: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const AssetCurveDialog = ({
	asset,
	open,
	onClose,
	onPrev,
	onNext,
	hasPrev,
	hasNext,
	modelId,
	runId,
}: AssetCurveDialogProps): JSX.Element | null => {
	const { t } = useGlobalization();

	const [curveData, setCurveData] = useState<CurvePoint[]>([]);
	const [weibullParams, setWeibullParams] = useState<WeibullParams | undefined>(undefined);
	const [isLoadingCurve, setIsLoadingCurve] = useState(false);
	const [curveError, setCurveError] = useState<string | null>(null);

	// Cache curves so navigating between assets of the same group is instant. Keyed by
	// model + run + group so switching runs (without remounting) never returns a stale curve.
	//
	// The fitted coefficients are cached with the points rather than held separately: they describe
	// this group's curve, so a cache hit has to restore both or the chart ends up labelled with
	// another group's fit.
	const curveCache = useRef<Map<string, { points: CurvePoint[]; params?: WeibullParams }>>(new Map());

	useEffect(() => {
		if (!asset || !open) return;

		const groupName = asset.assetGroupName;
		const groupId = asset.assetGroupId;
		const cacheKey = `${modelId}:${runId}:${groupId ?? groupName}`;

		const cached = curveCache.current.get(cacheKey);
		if (cached) {
			setCurveData(cached.points);
			setWeibullParams(cached.params);
			setCurveError(null);
			return;
		}

		setIsLoadingCurve(true);
		setCurveError(null);
		setCurveData([]);
		// Cleared alongside the points so a slow fetch cannot leave the previous group's
		// coefficients on screen next to an empty chart.
		setWeibullParams(undefined);

		const groupLookupRequest: GetDeteriorationModelGroupsResultsPaginatedRequests = {
			modelId,
			runId,
			page: 1,
			// Fetch the name matches and pick the exact group below; a slice of 1 could return
			// the wrong group when one name is a substring of another (e.g. "Main" vs "Main St").
			slice: 100,
			sortKey: 'assetGroupName',
			sortDescending: false,
			searchValue: groupName,
			filter: null,
			filterModel: null,
		};

		// The coefficients come from the group lookup but are only cached once the curve they
		// describe has arrived, so the two can never be stored apart.
		let fittedParams: WeibullParams | undefined;

		getDeteriorationModelGroupsResults(groupLookupRequest)
			.then(res => {
				const groups = res.data.data;
				// Prefer matching on the unique asset-group id; fall back to an exact name match,
				// then to the first result only as a last resort.
				const group =
					(groupId && groups.find(g => g.asset_group_id === groupId)) ||
					groups.find(g => g.asset_group_name === groupName) ||
					groups[0];
				if (!group) throw new Error(`No group result found for "${groupName}"`);
				// The group lookup is the only place the run's coefficients are available on this path.
				fittedParams = group.deterioration_params?.params;
				return getDeteriorationModelGroupsResultsChart(modelId, runId, group._id);
			})
			.then(res => {
				const points: CurvePoint[] = res.data.data.inference_results.map(r => ({
					age: r.age,
					fp: r.failure_probability * 100,
				}));
				curveCache.current.set(cacheKey, { points, params: fittedParams });
				setCurveData(points);
				setWeibullParams(fittedParams);
			})
			.catch(err => {
				console.error('Failed to load survival curve for group', groupName, err);
				setCurveError(t('Failed to load survival curve for this group.'));
			})
			.finally(() => {
				setIsLoadingCurve(false);
			});
	}, [asset?.assetGroupId, asset?.assetGroupName, open, modelId, runId]);

	const chartOptions = useMemo(
		(): Highcharts.Options =>
			!asset || curveData.length === 0
				? {}
				: buildCurveChartOptions(
						curveData,
						Number.parseFloat(asset.age ?? '0'),
						Number.parseFloat(asset.failureProbability ?? '0'),
						t,
				  ),
		[curveData, asset, t],
	);

	if (!asset) return null;

	const grade = asset.grade ?? '';
	const currentAge = Number.parseFloat(asset.age ?? '');
	const currentFP = Number.parseFloat(asset.failureProbability ?? '');
	const rul = Number.parseFloat(asset.rul ?? '');

	const renderRulValue = (): JSX.Element => {
		if (Number.isNaN(rul)) {
			return <Typography sx={{ fontSize: '1.375rem', fontWeight: 700 }}>—</Typography>;
		}
		if (rul <= 0) {
			return (
				<Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#dc2626', mt: 0.5 }}>{t('Passed')}</Typography>
			);
		}
		return (
			<Typography sx={{ fontSize: '1.375rem', fontWeight: 700, lineHeight: 1.2 }}>
				{rul.toFixed(1)}{' '}
				<Typography component="span" sx={{ fontSize: '0.875rem', fontWeight: 400, color: '#6b7280' }}>
					{t('yrs')}
				</Typography>
			</Typography>
		);
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
			{/* Header */}
			<Box
				sx={{
					px: 3,
					pt: 2.5,
					pb: 1.5,
					display: 'flex',
					alignItems: 'flex-start',
					justifyContent: 'space-between',
				}}>
				<Box>
					<Typography variant="h6" sx={{ fontWeight: 700 }}>
						{asset.assetId}
					</Typography>
					<Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
						{asset.assetGroupName}
					</Typography>
				</Box>

				<Stack direction="row" spacing={1} alignItems="center">
					<NavButton disabled={!hasPrev} onClick={onPrev}>
						<ArrowBackIosNewIcon sx={{ fontSize: 11 }} />
						{t('Previous')}
					</NavButton>
					<NavButton disabled={!hasNext} onClick={onNext}>
						{t('Next')}
						<ArrowForwardIosIcon sx={{ fontSize: 11 }} />
					</NavButton>
					<IconButton size="small" onClick={onClose} sx={{ ml: 0.5 }}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Stack>
			</Box>

			<Divider />

			<DialogContent sx={{ pt: 2, pb: 3 }}>
				{/* KPI Row */}
				<Grid container spacing={3} sx={{ mb: 3 }}>
					<Grid item xs={3}>
						<KpiItem
							label={t('Failure Grade')}
							tooltip={t(
								'Classification based on Failure Probability and Remaining Useful Life thresholds. Adjust thresholds in Grade Setup.',
							)}>
							{GRADE_CONFIG[grade] ? (
								<GradeChip grade={grade} sx={{ alignSelf: 'flex-start', py: '3px', mt: 0.5 }} />
							) : (
								<Typography sx={{ fontSize: '1.375rem', fontWeight: 700 }}>—</Typography>
							)}
						</KpiItem>
					</Grid>

					<Grid item xs={3}>
						<KpiItem label={t('Current Age')}>
							<Typography sx={{ fontSize: '1.375rem', fontWeight: 700, lineHeight: 1.2 }}>
								{Number.isNaN(currentAge) ? '—' : currentAge.toFixed(1)}{' '}
								<Typography component="span" sx={{ fontSize: '0.875rem', fontWeight: 400, color: '#6b7280' }}>
									{t('yrs')}
								</Typography>
							</Typography>
						</KpiItem>
					</Grid>

					<Grid item xs={3}>
						<KpiItem
							label={t('Failure Probability')}
							tooltip={t(
								'The likelihood that this asset has experienced or will experience failure at its current age, based on the survival curve of its group. Higher values indicate greater risk.',
							)}>
							<Typography sx={{ fontSize: '1.375rem', fontWeight: 700, lineHeight: 1.2 }}>
								{Number.isNaN(currentFP) ? '—' : currentFP.toFixed(1)}
								<Typography component="span" sx={{ fontSize: '0.875rem', fontWeight: 400, color: '#6b7280' }}>
									%
								</Typography>
							</Typography>
						</KpiItem>
					</Grid>

					<Grid item xs={3}>
						<KpiItem
							label={t('RUL (80%)')}
							tooltip={t(
								'Estimated years until this asset reaches 80% failure probability (Failure Threshold). Negative values mean the asset has already passed this threshold.',
							)}>
							{renderRulValue()}
						</KpiItem>
					</Grid>
				</Grid>

				{/* Chart Section */}
				<Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, p: 2 }}>
					<Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', mb: 0.25 }}>{t('Failure Probability')}</Typography>
					<Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 1.5 }}>
						{t(
							// eslint-disable-next-line prettier/prettier
							'Probability of failure over time, based on the survival curve assigned to this asset\'s group',
						)}
					</Typography>

					{isLoadingCurve && (
						<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
							<CircularProgress size={32} />
						</Box>
					)}

					{!isLoadingCurve && curveError && (
						<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
							<Typography variant="body2" sx={{ color: '#9ca3af' }}>
								{curveError}
							</Typography>
						</Box>
					)}

					{!isLoadingCurve && !curveError && curveData.length > 0 && (
						<>
							<HighchartsReact
								highcharts={Highcharts}
								options={chartOptions}
								containerProps={{ style: { height: '300px', width: '100%' } }}
							/>
							<WeibullFitNotes isFlat={isFlatSeries(curveData.map(point => point.fp))} params={weibullParams} />
							<Stack direction="row" spacing={3} sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #e5e7eb' }}>
								<Stack direction="row" spacing={1} alignItems="center">
									<Box sx={{ width: 24, height: 2, backgroundColor: '#3b82f6', flexShrink: 0 }} />
									<Typography variant="caption" sx={{ color: '#6b7280' }}>
										{t('Failure Probability Curve')}
									</Typography>
								</Stack>
								<Stack direction="row" spacing={1} alignItems="center">
									<Box sx={{ width: 24, height: 0, borderTop: '2px dashed #3b82f6', flexShrink: 0 }} />
									<Typography variant="caption" sx={{ color: '#6b7280' }}>
										{t('Projection')}
									</Typography>
								</Stack>
								<Stack direction="row" spacing={1} alignItems="center">
									<Box sx={{ width: 18, height: 0, borderTop: '2px dashed #dc2626', flexShrink: 0 }} />
									<Typography variant="caption" sx={{ color: '#6b7280' }}>
										{t('80% Failure Threshold')}
									</Typography>
								</Stack>
							</Stack>
						</>
					)}
				</Box>

				{/* Summary */}
				<Box sx={{ mt: 2, p: 2, backgroundColor: '#f9fafb', borderRadius: 1, border: '1px solid #e5e7eb' }}>
					<Typography variant="body2" sx={{ color: '#374151' }}>
						<Box component="span" sx={{ fontWeight: 600 }}>
							{t('Summary:')}
						</Box>{' '}
						{t('This asset is {{age}} years old with a {{fp}}% failure probability.', {
							age: Number.isNaN(currentAge) ? '?' : currentAge.toFixed(1),
							fp: Number.isNaN(currentFP) ? '?' : currentFP.toFixed(1),
						})}{' '}
						{!Number.isNaN(rul) &&
							(rul <= 0
								? t('It has already passed the 80% failure threshold.')
								: t('It will reach the 80% failure threshold in approximately {{rul}} years.', {
										rul: rul.toFixed(1),
								  }))}
					</Typography>
				</Box>
			</DialogContent>
		</Dialog>
	);
};

// =============================================================================
// SMALL HELPERS
// =============================================================================

interface KpiItemProps {
	label: string;
	tooltip?: string;
	children: React.ReactNode;
}

const KpiItem = ({ label, tooltip, children }: KpiItemProps): JSX.Element => (
	<Stack spacing={0.5}>
		<Stack direction="row" spacing={0.5} alignItems="center">
			<Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>
				{label}
			</Typography>
			{tooltip && (
				<InfoTooltip content={tooltip} placement="top">
					<HelpOutlineIcon sx={{ fontSize: 14, color: '#9ca3af', cursor: 'pointer' }} />
				</InfoTooltip>
			)}
		</Stack>
		{children}
	</Stack>
);

interface NavButtonProps {
	disabled: boolean;
	onClick: () => void;
	children: React.ReactNode;
}

const NavButton = ({ disabled, onClick, children }: NavButtonProps): JSX.Element => (
	<Box
		component="button"
		disabled={disabled}
		onClick={onClick}
		sx={{
			display: 'flex',
			alignItems: 'center',
			gap: '4px',
			px: 1.5,
			py: 0.75,
			borderRadius: 1,
			border: '1px solid #e5e7eb',
			background: '#fff',
			cursor: disabled ? 'not-allowed' : 'pointer',
			fontSize: '0.8125rem',
			color: disabled ? '#d1d5db' : '#374151',
			fontFamily: 'inherit',
			'&:hover:not(:disabled)': { backgroundColor: '#f9fafb' },
		}}>
		{children}
	</Box>
);
